import React, { useState, useRef, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useCompany } from '../contexts/CompanyContext';
import { useAuth } from '../../auth/contexts/AuthContext';
import { useToast } from '../../shared/contexts/ToastContext';
import { Company } from '../types/Company';

interface EditCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  company: Company | null;
  onUpdate: (updatedCompany: Company) => void;
}

const EditCompanyModal: React.FC<EditCompanyModalProps> = ({ isOpen, onClose, company, onUpdate }) => {
  const [companyName, setCompanyName] = useState('');
  const [glossaryFile, setGlossaryFile] = useState<File | null>(null);
  /* NORMALIZE FIELD - Uncomment to enable
  const [normalizeFile, setNormalizeFile] = useState<File | null>(null);
  */
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasGlossaryFile, setHasGlossaryFile] = useState(false);
  const [isCheckingGlossary, setIsCheckingGlossary] = useState(false);
  const [errors, setErrors] = useState<{
    glossary?: string;
    /* NORMALIZE FIELD - Uncomment to enable
    normalize?: string;
    */
  }>({});

  const { updateCompany, refreshCompanies, downloadGlossaryFile } = useCompany();
  const { user } = useAuth();
  const { success, error, warning } = useToast();

  const glossaryInputRef = useRef<HTMLInputElement>(null);
  /* NORMALIZE FIELD - Uncomment to enable
  const normalizeInputRef = useRef<HTMLInputElement>(null);
  */

  // Initialize form with company data when modal opens
  useEffect(() => {
    if (isOpen && company) {
      setCompanyName(company.name);
      setGlossaryFile(null);
      /* NORMALIZE FIELD - Uncomment to enable
      setNormalizeFile(null);
      */
      setErrors({});
      // Clear file inputs
      if (glossaryInputRef.current) glossaryInputRef.current.value = '';
      /* NORMALIZE FIELD - Uncomment to enable
      if (normalizeInputRef.current) normalizeInputRef.current.value = '';
      */
      
      // Check if glossary file exists
      checkGlossaryExists();
    }
  }, [isOpen, company]);

  // Check if company has a glossary file
  const checkGlossaryExists = async () => {
    if (!company) return;
    
    // If company already has glossaryFileName, we know it exists
    if (company.glossaryFileName) {
      setHasGlossaryFile(true);
      return;
    }
    
    // Otherwise, check with the endpoint
    setIsCheckingGlossary(true);
    try {
      const company_id = company.company_id || company.id;
      console.log('Glossary varlığı kontrol ediliyor, company_id:', company_id);
      
      const response = await fetch('https://n8n.mahir.ai/webhook/get_glossary', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company_id: company_id
        })
      });
      
      console.log('Glossary kontrol response:', response.status);
      
      if (response.ok) {
        setHasGlossaryFile(true);
        console.log('Glossary dosyası var');
      } else {
        setHasGlossaryFile(false);
        console.log('Glossary dosyası yok');
      }
    } catch (error) {
      console.error('Glossary kontrolü sırasında hata:', error);
      setHasGlossaryFile(false);
    } finally {
      setIsCheckingGlossary(false);
    }
  };

  const validateForm = () => {
    const newErrors: typeof errors = {};

    // File validation only if files are provided (optional)
    if (glossaryFile && !glossaryFile.name.toLowerCase().endsWith('.xlsx') && !glossaryFile.name.toLowerCase().endsWith('.xls')) {
      newErrors.glossary = 'Sadece Excel dosyaları (.xlsx, .xls) kabul edilir';
    }

    /* NORMALIZE FIELD - Uncomment to enable
    if (normalizeFile && !normalizeFile.name.toLowerCase().endsWith('.xlsx') && !normalizeFile.name.toLowerCase().endsWith('.xls')) {
      newErrors.normalize = 'Sadece Excel dosyaları (.xlsx, .xls) kabul edilir';
    }
    */

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || !company) {
      return;
    }

    await submitUpdate();
  };

  const submitUpdate = async () => {
    if (!company) return;
    
    setIsSubmitting(true);
    try {
      let glossarySuccess = true;
      /* NORMALIZE FIELD - Uncomment to enable
      let normalizeSuccess = true;
      */

      // Send glossary file to webhook if selected
      if (glossaryFile) {
        try {
          const glossaryId = uuidv4();
          const glossaryFormData = new FormData();
          glossaryFormData.append('company_id', company.company_id?.toString() || company.id);
          glossaryFormData.append('company_name', company.name);
          glossaryFormData.append('user_id', user?.id || 'unknown');
          glossaryFormData.append('glossary_id', glossaryId);
          glossaryFormData.append('glossary', glossaryFile);
          glossaryFormData.append('create_date', new Date().toISOString().replace('T', ' ').replace('Z', ''));

          console.log('Glossary webhook\'a gönderilen veriler:', {
            company_id: company.company_id?.toString() || company.id,
            company_name: company.name,
            user_id: user?.id || 'unknown',
            glossary_id: glossaryId,
            glossary_file: glossaryFile.name,
            create_date: new Date().toISOString().replace('T', ' ').replace('Z', '')
          });

          const glossaryResponse = await fetch('https://n8n.mahir.ai/webhook/edit_glossary', {
            method: 'POST',
            body: glossaryFormData
          });

          if (glossaryResponse.ok) {
            console.log('Glossary başarıyla güncellendi');
          } else {
            console.error('Glossary güncelleme hatası:', glossaryResponse.status, await glossaryResponse.text());
            glossarySuccess = false;
          }
        } catch (glossaryError) {
          console.error('Glossary webhook çağrısında hata:', glossaryError);
          glossarySuccess = false;
        }
      }

      /* NORMALIZE FIELD - Uncomment to enable
      // Send normalize file to webhook if selected
      if (normalizeFile) {
        try {
          const normalizeId = uuidv4();
          const normalizeFormData = new FormData();
          normalizeFormData.append('company_id', company.company_id?.toString() || company.id);
          normalizeFormData.append('company_name', company.name);
          normalizeFormData.append('user_id', user?.id || 'unknown');
          normalizeFormData.append('normalize_id', normalizeId);
          normalizeFormData.append('normalize', normalizeFile);
          normalizeFormData.append('create_date', new Date().toISOString().replace('T', ' ').replace('Z', ''));

          console.log('Normalize webhook\'a gönderilen veriler:', {
            company_id: company.company_id?.toString() || company.id,
            company_name: company.name,
            user_id: user?.id || 'unknown',
            normalize_id: normalizeId,
            normalize_file: normalizeFile.name,
            create_date: new Date().toISOString().replace('T', ' ').replace('Z', '')
          });

          const normalizeResponse = await fetch('https://n8n.mahir.ai/webhook/edit_normalize', {
            method: 'POST',
            body: normalizeFormData
          });

          if (normalizeResponse.ok) {
            console.log('Normalize başarıyla güncellendi');
          } else {
            console.error('Normalize güncelleme hatası:', normalizeResponse.status, await normalizeResponse.text());
            normalizeSuccess = false;
          }
        } catch (normalizeError) {
          console.error('Normalize webhook çağrısında hata:', normalizeError);
          normalizeSuccess = false;
        }
      }
      */

      // Show results to user
      /* NORMALIZE FIELD - Uncomment the multifile logic below and remove the glossary-only logic
      if (glossaryFile && normalizeFile) {
        if (glossarySuccess && normalizeSuccess) {
          success('Dosyalar Güncellendi!', 'Hem glossary hem de normalize dosyaları başarıyla güncellendi.');
        } else if (glossarySuccess && !normalizeSuccess) {
          warning('Kısmi Güncelleme', 'Glossary dosyası güncellendi, ancak normalize dosyası güncellenirken hata oluştu.');
        } else if (!glossarySuccess && normalizeSuccess) {
          warning('Kısmi Güncelleme', 'Normalize dosyası güncellendi, ancak glossary dosyası güncellenirken hata oluştu.');
        } else {
          error('Güncelleme Hatası', 'Her iki dosya güncellenirken de hata oluştu.');
        }
      } else 
      */
      if (glossaryFile) {
        if (glossarySuccess) {
          success('Glossary Güncellendi!', 'Glossary dosyası başarıyla güncellendi.');
        } else {
          error('Glossary Hatası', 'Glossary dosyası güncellenirken hata oluştu.');
        }
      }
      /* NORMALIZE FIELD - Uncomment to enable
      else if (normalizeFile) {
        if (normalizeSuccess) {
          success('Normalize Güncellendi!', 'Normalize dosyası başarıyla güncellendi.');
        } else {
          error('Normalize Hatası', 'Normalize dosyası güncellenirken hata oluştu.');
        }
      }
      */

      // Update local state only if at least one operation was successful
      /* NORMALIZE FIELD - Update condition: if ((glossaryFile && glossarySuccess) || (normalizeFile && normalizeSuccess) || (!glossaryFile && !normalizeFile)) { */
      if ((glossaryFile && glossarySuccess) || !glossaryFile) {
        // Create updated company object (name stays the same)
        const updatedCompany: Company = {
          ...company,
          updatedAt: new Date(),
          // Add file names if files were successfully updated
          ...(glossaryFile && glossarySuccess && { glossaryFileName: glossaryFile.name }),
          /* NORMALIZE FIELD - Uncomment to enable
          ...(normalizeFile && normalizeSuccess && { normalizeFileName: normalizeFile.name }),
          */
        };

        // Update company in context (only successful files)
        const updateData: Partial<Company> = {};
        if (glossaryFile && glossarySuccess) {
          updateData.glossaryFileName = glossaryFile.name;
        }
        /* NORMALIZE FIELD - Uncomment to enable
        if (normalizeFile && normalizeSuccess) {
          updateData.normalizeFileName = normalizeFile.name;
        }
        */
        
        if (Object.keys(updateData).length > 0) {
          updateCompany(company.id, updateData);
        }

        // Call parent update handler
        onUpdate(updatedCompany);

        // Refresh companies from webhook
        setTimeout(async () => {
          await refreshCompanies();
        }, 1000);
      }

      handleClose();
    } catch (err) {
      console.error('Şirket güncellenirken hata:', err);
      error('Beklenmeyen Hata', 'Şirket güncellenirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleClose = () => {
    setCompanyName('');
    setGlossaryFile(null);
    /* NORMALIZE FIELD - Uncomment to enable
    setNormalizeFile(null);
    */
    setErrors({});
    setIsSubmitting(false);
    setHasGlossaryFile(false);
    setIsCheckingGlossary(false);
    if (glossaryInputRef.current) glossaryInputRef.current.value = '';
    /* NORMALIZE FIELD - Uncomment to enable
    if (normalizeInputRef.current) normalizeInputRef.current.value = '';
    */
    onClose();
  };

  const handleFileChange = (type: 'glossary' /* NORMALIZE FIELD: | 'normalize' */, file: File | null) => {
    if (type === 'glossary') {
      setGlossaryFile(file);
      setErrors(prev => ({ ...prev, glossary: undefined }));
    }
    /* NORMALIZE FIELD - Uncomment to enable
    else {
      setNormalizeFile(file);
      setErrors(prev => ({ ...prev, normalize: undefined }));
    }
    */
  };

  if (!isOpen || !company) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-8 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Şirketi Düzenle
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {company.name} bilgilerini güncelle
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-gray-50/50 dark:bg-gray-900/50 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              Temel Bilgiler
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Şirket Adı
                </label>
                <p className="text-gray-900 dark:text-white font-medium">
                  {company.name}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Şirket ID
                </label>
                <p className="text-gray-900 dark:text-white font-mono text-sm">
                  {company.id}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Oluşturulma Tarihi
                </label>
                <p className="text-gray-900 dark:text-white text-sm">
                  {new Date(company.createdAt).toLocaleDateString('tr-TR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Son Güncellenme
                </label>
                <p className="text-gray-900 dark:text-white text-sm">
                  {new Date(company.updatedAt).toLocaleDateString('tr-TR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
            </div>
            
            {/* Created By Information */}
            {(company.created_by_username || (company.createdBy && company.createdBy.name)) && (
              <div className="mt-4 p-4 bg-blue-50/50 dark:bg-blue-900/20 rounded-lg border border-blue-200/50 dark:border-blue-800/50">
                <label className="block text-sm font-medium text-blue-700 dark:text-blue-300 mb-2 flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                  </svg>
                  Oluşturan Kişi
                </label>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 dark:text-blue-400 font-semibold text-sm">
                      {(company.created_by_username || company.createdBy?.name || 'U').charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="text-blue-900 dark:text-blue-100 font-medium">
                      {company.created_by_username || company.createdBy?.name || 'Bilinmeyen'}
                    </p>
                    {company.createdBy?.email && (
                      <p className="text-blue-600 dark:text-blue-400 text-sm">
                        {company.createdBy.email}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Current Files Info */}
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Mevcut Dosyalar</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-gray-600 dark:text-gray-400">Glossary:</span>
                  {isCheckingGlossary ? (
                    <span className="text-gray-400 animate-pulse">Kontrol ediliyor...</span>
                  ) : (
                    <span className={`font-medium ${hasGlossaryFile ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
                      {hasGlossaryFile ? (company.glossaryFileName || `${company.name}_glossary.xlsx`) : 'Yok'}
                    </span>
                  )}
                </div>
                {hasGlossaryFile && !isCheckingGlossary && (
                  <button
                    type="button"
                    onClick={async () => {
                      console.log('İndir butonuna tıklandı, company:', company);
                      try {
                        await downloadGlossaryFile(company);
                        success('Başarılı', 'Glossary dosyası indiriliyor...');
                      } catch (err) {
                        console.error('Glossary indirme hatası:', err);
                        error('Hata', 'Glossary dosyası indirilirken bir hata oluştu.');
                      }
                    }}
                    className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-md transition-colors flex items-center space-x-1"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                    </svg>
                    <span>İndir</span>
                  </button>
                )}
              </div>
              {/* NORMALIZE FIELD - Uncomment to enable
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">Normalize:</span>
                <span className={`font-medium ${company.normalizeFileName ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
                  {company.normalizeFileName || 'Yok'}
                </span>
              </div>
              */}
            </div>
          </div>

          {/* Glossary File */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Yeni Glossary Dosyası (Excel) - Opsiyonel
            </label>
            <div className="relative">
              <input
                ref={glossaryInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => handleFileChange('glossary', e.target.files?.[0] || null)}
                className="hidden"
                id="glossary-upload-edit"
              />
              <label
                htmlFor="glossary-upload-edit"
                className={`flex items-center justify-center w-full px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700 ${
                  errors.glossary
                    ? 'border-red-500 dark:border-red-500'
                    : glossaryFile
                    ? 'border-green-500 dark:border-green-500 bg-green-50 dark:bg-green-900/20'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
              >
                <div className="text-center">
                  <svg className={`mx-auto h-8 w-8 mb-2 ${
                    glossaryFile ? 'text-green-500' : 'text-gray-400'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {glossaryFile ? glossaryFile.name : 'Yeni glossary dosyası seçin (opsiyonel)'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    Excel formatında (.xlsx, .xls)
                  </p>
                </div>
              </label>
            </div>
            {errors.glossary && (
              <p className="mt-1 text-sm text-red-500">{errors.glossary}</p>
            )}
          </div>

          {/* NORMALIZE FIELD - Uncomment to enable
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Yeni Normalize Dosyası (Excel) - Opsiyonel
            </label>
            <div className="relative">
              <input
                ref={normalizeInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => handleFileChange('normalize', e.target.files?.[0] || null)}
                className="hidden"
                id="normalize-upload-edit"
              />
              <label
                htmlFor="normalize-upload-edit"
                className={`flex items-center justify-center w-full px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700 ${
                  errors.normalize
                    ? 'border-red-500 dark:border-red-500'
                    : normalizeFile
                    ? 'border-green-500 dark:border-green-500 bg-green-50 dark:bg-green-900/20'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
              >
                <div className="text-center">
                  <svg className={`mx-auto h-8 w-8 mb-2 ${
                    normalizeFile ? 'text-green-500' : 'text-gray-400'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {normalizeFile ? normalizeFile.name : 'Yeni normalize dosyası seçin (opsiyonel)'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    Excel formatında (.xlsx, .xls)
                  </p>
                </div>
              </label>
            </div>
            {errors.normalize && (
              <p className="mt-1 text-sm text-red-500">{errors.normalize}</p>
            )}
          </div>
          */}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-colors disabled:opacity-50"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                  Güncelleniyor...
                </>
              ) : (
                'Güncelle'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCompanyModal;
